import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  CreateModelSkillRequestBody,
  ListModelSkillsResponse,
  UpdateModelSkillRequestBody,
  UpdateProcessRequestBody,
  modelSkillSortMap,
  OrderBy,
} from "common";

// Helper to convert frontend filters to backend filter format
// Backend format: "processId:eq=123,ownerId:eq=1"
const buildFilterString = (
  filters: Array<{ id: string; value: unknown }>
): string => {
  const filterParts: string[] = [];

  filters.forEach(filter => {
    if (
      filter.value === "" ||
      filter.value === null ||
      filter.value === undefined
    ) {
      return;
    }

    // Map frontend filter IDs to backend filter names and operators
    const filterMap: Record<string, { field: string; operator: string }> = {
      processId: { field: "processId", operator: "eq" },
      ownerId: { field: "ownerId", operator: "eq" },
    };

    const filterConfig = filterMap[filter.id];
    if (filterConfig) {
      filterParts.push(
        `${filterConfig.field}:${filterConfig.operator}=${filter.value}`
      );
    }
  });

  return filterParts.join(",");
};

// Helper to convert frontend sort to backend sort
const getSortField = (sortId: string): keyof typeof modelSkillSortMap => {
  // Map frontend sort IDs to backend sort fields
  const sortMap: Record<string, keyof typeof modelSkillSortMap> = {
    createdAt: "createdAt",
    updatedAt: "updatedAt",
    processId: "processId",
  };

  return sortMap[sortId] || "createdAt";
};

export const fetchModelSkillsList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter: Array<{ id: string; value: unknown }>
): Promise<ListModelSkillsResponse> => {
  try {
    const baseUrl = "/api/v1/cortex/model-skill/list";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    // Add sort
    if (sort && sort.length > 0) {
      const sortField = getSortField(sort[0].id);
      queryParams.append("sort", sortField);
      queryParams.append("order", sort[0].desc ? OrderBy.Desc : OrderBy.Asc);
    } else {
      queryParams.append("sort", "createdAt");
      queryParams.append("order", OrderBy.Desc);
    }

    // Add filter
    const filterString = buildFilterString(filter);
    if (filterString) {
      queryParams.append("filter", filterString);
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get<ListModelSkillsResponse>(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useModelSkillsList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): any => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/v1/cortex/model-skill/list", take, skip, sort, filter],
    queryFn: () => fetchModelSkillsList(take, skip, sort, filter),
    retry: false,
    enabled,
  });
};

export const useCreateModelSkill = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: CreateModelSkillRequestBody) => {
      const res = await axios.post("/api/v1/cortex/model-skill", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "model-skill-created",
        message: "Model Skill Successfully Created",
        details: "You can now use this model skill",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/model-skill/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "model-skill-creation-failed",
        message: "Model Skill Creation Failed",
        details:
          error.response?.data?.message ?? "Model skill could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateModelSkill = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      processId,
      payload,
    }: {
      processId: number;
      payload: UpdateModelSkillRequestBody;
    }) => {
      // Extract the process data from the payload
      const processPayload: UpdateProcessRequestBody = payload.process;
      const res = await axios.put(
        `/api/v1/process/${processId}`,
        processPayload
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "model-skill-updated",
        message: "Model Skill Successfully Updated",
        details: "Your changes have been saved",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/model-skill/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "model-skill-update-failed",
        message: "Model Skill Update Failed",
        details:
          error.response?.data?.message ??
          "We were unable to save your changes",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteModelSkill = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.delete(`/api/v1/cortex/model-skill/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "model-skill-deleted",
        message: "Model Skill Successfully Deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/model-skill/list"],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.message ?? "Failed to delete model skill";
      setNotification({
        id: "model-skill-delete-failed",
        message: "Delete Model Skill Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteModelSkillRevision = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (processId: number): Promise<void> => {
      await axios.delete(`/api/v1/process/${processId}`);
    },
    onSuccess: (): void => {
      setNotification({
        id: "delete-model-skill-revision-success",
        message: "Model Skill Revision Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/v1/cortex/model-skill/list"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["processRevisions"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-model-skill-revision-failed",
        message: "Model Skill Revision Deletion Failed",
        details:
          error?.response?.data?.message ??
          "We were unable to delete the revision",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
